import React, { FC, ReactNode, useContext } from 'react'
import type { InputProps } from 'antd/lib/input'
import type { TreeNode, TreeSelectProps } from 'antd/lib/tree-select'
import type { CascaderOptionType, CascaderProps } from 'antd/lib/cascader'
import Tag from '../tag'
import cc from 'classnames'
import { isUndefined, isArray, isNull, isFunction } from 'lodash'
import type { DatePickerProps, MonthPickerProps, WeekPickerProps } from '../date-picker'
import type { SelectProps } from '../select'
import { FormContext } from '../tntd-form/context'
import type { TimePickerProps } from 'antd/lib/time-picker'

const prefixCls = 'tnt-preview-text'

const isValid = (value?: unknown) => !isUndefined(value) && !isNull(value) && value !== ''

const usePlaceholder = (value?: unknown): string | ReactNode => {
  const { placeholder } = useContext(FormContext)

  return isValid(value) ? (value as string) : placeholder
}

const Input: React.FC<InputProps> = (props) => (
  <div className={cc([props.className])} style={props.style}>
    {props.addonBefore}
    {props.prefix}
    {usePlaceholder(props.value)}
    {props.suffix}
    {props.addonAfter}
  </div>
)

const Select: FC<SelectProps> = ({ options, mode, value, labelInValue, className, style }) => {
  const dataSource = (options || []) as Array<{
    label: string
    value: string
  }>
  const placeholder = usePlaceholder()
  const isMultiple = ['multiple', 'tags'].includes(mode ?? '') || isArray(value)
  const getSelected = () => {
    if (isMultiple) {
      if (labelInValue) {
        return isArray(value) ? value : []
      } else {
        return isArray(value) ? value.map((val) => ({ label: val, value: val })) : []
      }
    } else {
      if (labelInValue) {
        return isValid(value) ? [value] : []
      } else {
        return isValid(value) ? [{ label: value, value }] : []
      }
    }
  }

  const getLabels = (): string | ReactNode => {
    const selected = getSelected() as Array<{ label: string; value: string }>
    if (!selected.length) return placeholder
    return selected.map(({ label, value }, key) => {
      const text = dataSource?.find((item) => item.value === value)?.label || label
      return isMultiple ? <Tag key={key}>{text || placeholder}</Tag> : text || placeholder
    })
  }
  return (
    <div className={cc([prefixCls, className])} style={style}>
      {getLabels()}
    </div>
  )
}

const TreeSelect: React.FC<TreeSelectProps<any>> = ({
  value,
  multiple,
  labelInValue,
  treeData,
  className,
  style,
}) => {
  const placeholder = usePlaceholder()
  const dataSource: TreeNode[] = treeData || []
  const getSelected = () => {
    if (multiple) {
      if (labelInValue) {
        return isArray(value) ? value : []
      } else {
        return isArray(value) ? value.map((val) => ({ label: val, value: val })) : []
      }
    } else {
      if (labelInValue) {
        return value ? [value] : []
      } else {
        return value ? [{ label: value, value }] : []
      }
    }
  }

  const findLabel = (value: string, dataSource: TreeNode[]): unknown => {
    for (let i = 0; i < dataSource?.length; i++) {
      const item = dataSource[i]
      if (item?.value === value || item?.key === value) {
        return item?.title
      } else {
        const childLabel = findLabel(value, item?.children as TreeNode[])
        if (childLabel) return childLabel
      }
    }
  }

  const getLabels = () => {
    const selected = getSelected()
    if (!selected?.length) return <Tag>{placeholder as string}</Tag>
    return selected.map(({ value, label }, key) => {
      return (
        <>
          {multiple ? (
            <Tag key={key}>{findLabel(value, dataSource) || label || placeholder}</Tag>
          ) : (
            findLabel(value, dataSource) || label || placeholder
          )}
        </>
      )
    })
  }
  return (
    <div className={cc([prefixCls, className])} style={style}>
      {getLabels()}
    </div>
  )
}

const Cascader: React.FC<CascaderProps> = ({ options, value, className, style }) => {
  const placeholder = usePlaceholder()
  const dataSource: CascaderOptionType[] = options || []
  const getSelected = () => {
    return isArray(value) ? value : []
  }
  const findLabel = (value: any, dataSource: any[]): any => {
    for (let i = 0; i < dataSource?.length; i++) {
      const item = dataSource[i]
      if (item?.value === value) {
        return item?.label
      } else {
        const childLabel = findLabel(value, item?.children)
        if (childLabel) return childLabel
      }
    }
  }
  const getLabels = () => {
    const selected = getSelected()
    if (!selected?.length) {
      return placeholder
    }
    return selected
      .map((value) => {
        return findLabel(value, dataSource) || placeholder
      })
      .join('/')
  }
  return (
    <div className={cc([prefixCls, className])} style={style}>
      {getLabels()}
    </div>
  )
}

const formatMomentValue = (value: any, format: any, placeholder?: string): string | string[] => {
  const formatDate = (date: any, format: any, i = 0) => {
    if (!date) return placeholder
    if (isArray(format)) {
      const _format = format[i]
      if (isFunction(_format)) {
        return _format(date)
      }
      return date?.format ? date.format(_format) : date
    } else {
      if (isFunction(format)) {
        return format(date)
      }
      return date?.format ? date.format(format) : date
    }
  }
  if (isArray(value)) {
    return value.map((val, index) => {
      return formatDate(val, format, index)
    })
  } else {
    return value ? formatDate(value, format) : value || placeholder
  }
}

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  format,
  showTime,
  placeholder,
  className,
}) => {
  const getLabels = () => {
    const labels = formatMomentValue(
      value,
      format || `YYYY-MM-DD${showTime ? ' HH:mm:ss' : ''}`,
      placeholder
    )
    return isArray(labels) ? labels.join('~') : labels
  }
  return <div className={cc([prefixCls, className])}>{getLabels()}</div>
}

const TimePicker: React.FC<TimePickerProps> = ({
  value,
  format,
  placeholder,
  className,
  style,
}) => {
  const getLabels = () => {
    const labels = formatMomentValue(value, format || 'HH:mm:ss', placeholder)
    return isArray(labels) ? labels.join('~') : labels
  }
  return (
    <div className={cc(prefixCls, className)} style={style}>
      {getLabels()}
    </div>
  )
}

const MonthPicker: React.FC<MonthPickerProps> = ({
  value,
  format,
  placeholder,
  className,
  style,
}) => {
  const getLabels = () => {
    const labels = formatMomentValue(value, format || 'YYYY-MM', placeholder)
    return isArray(labels) ? labels.join('~') : labels
  }
  return (
    <div className={cc(prefixCls, className)} style={style}>
      {getLabels()}
    </div>
  )
}

const WeekPicker: React.FC<WeekPickerProps> = ({
  value,
  format,
  placeholder,
  className,
  style,
}) => {
  const getLabels = () => {
    const labels = formatMomentValue(value, format || 'gggg-wo', placeholder)
    return isArray(labels) ? labels.join('~') : labels
  }
  return (
    <div className={cc(prefixCls, className)} style={style}>
      {getLabels()}
    </div>
  )
}

export const PreviewText = {
  Input,
  Select,
  TreeSelect,
  Cascader,
  DatePicker,
  TimePicker,
  MonthPicker,
  WeekPicker,
  usePlaceholder,
}

export default PreviewText
